import type { LeadOptionsPreferences } from "../preferences/contracts";

/**
 * Listas de Origens e Segmentos oferecidas no cadastro do lead.
 *
 * Origem e segmento continuam texto livre no lead (§14.4): a lista é sugestão,
 * não enum. O que a pessoa controla em Configurações é quais sugestões
 * aparecem — pode adicionar à mão e apagar. Valores novos digitados num
 * cadastro entram sozinhos, porque passam a estar em uso, a menos que tenham
 * sido apagados da lista de propósito.
 *
 * Apagar uma opção nunca reescreve lead nenhum: os cadastros que já usam o
 * valor continuam com ele; ele só deixa de ser sugerido.
 */

export type LeadOptionKind = "sources" | "segments";

const REMOVED: Record<LeadOptionKind, "removedSources" | "removedSegments"> = {
  sources: "removedSources",
  segments: "removedSegments",
};

const key = (value: string) => value.trim().toLocaleLowerCase("pt-BR");

/** Valores distintos, sem vazios, na ordem em que aparecem. */
export function distinctValues(values: Iterable<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of values) {
    const value = raw?.trim();
    if (!value) continue;
    const k = key(value);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(value);
  }
  return out;
}

/** A lista efetiva: a ordem salva primeiro, depois o que está em uso e não foi apagado. */
export function effectiveLeadOptions(
  preferences: LeadOptionsPreferences,
  inUse: readonly string[],
  kind: LeadOptionKind,
): string[] {
  const saved = distinctValues(preferences[kind]);
  const removed = new Set(preferences[REMOVED[kind]].map(key));
  const known = new Set(saved.map(key));
  const extra = distinctValues(inUse).filter((value) => !known.has(key(value)) && !removed.has(key(value)));
  return [...saved, ...extra.sort((a, b) => a.localeCompare(b, "pt-BR"))];
}

/**
 * Preferência depois de salvar a lista editada.
 *
 * O que saiu da lista efetiva passa a ser apagado de propósito; o que voltou
 * à lista deixa de ser.
 */
export function nextLeadOptions(
  preferences: LeadOptionsPreferences,
  inUse: readonly string[],
  kind: LeadOptionKind,
  names: readonly string[],
): LeadOptionsPreferences {
  const list = distinctValues(names);
  const kept = new Set(list.map(key));
  const before = effectiveLeadOptions(preferences, inUse, kind);
  const removed = distinctValues([
    ...preferences[REMOVED[kind]],
    ...before.filter((value) => !kept.has(key(value))),
  ]).filter((value) => !kept.has(key(value)));
  return { ...preferences, [kind]: list, [REMOVED[kind]]: removed };
}
